import {
    Box,
    Button,
    Dialog,
    DialogContent,
    styled,
    Typography,
} from '@mui/material';
import { ModuleT } from 'business/constants';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';
import Loading from 'components/Loading';
import CustomizationForm from '../ClientSupport/components/ClientConfiguration/CustomizationForm/CustomizationForm';
import IntegrationForm from '../ClientSupport/components/ClientConfiguration/IntegrationForm/IntegrationForm';
import { ExtraFeatureT } from '../ClientSupport/components/ClientConfiguration/ModuleForm/helpers/definitions';
import { ClientPalette } from '../ClientSupport/components/ClientConfiguration/ModuleForm/helpers/types';
import ModuleForm from '../ClientSupport/components/ClientConfiguration/ModuleForm/ModuleForm';
import { ClientDetailsForm } from './ClientDetailsForm';
import { ClientPassword } from './ClientPasswords';
import { useCreateClientMutation } from './hooks/useCreateClientMutation';
import { SupportModalTitle } from '../SupportModalTitle';
import UsageHistoryForm from '../ClientSupport/components/ClientConfiguration/UsageHistoryForm/UsageHistoryForm';

const StyItem = styled(Box, { name: 'StyItem' })(() => ({
    marginLeft: '10px',
    marginTop: '10px',
}));

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

export const CreateClientModal = ({ isOpen, onClose }: Props) => {
    const { t } = useTranslation();

    const initialPalette: ClientPalette = {
        primaryColor: undefined,
        secondaryColor: undefined,
        logoUrl: '',
    };

    const [bind, setBind] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [decisionPassword, setDecisionPassword] = useState('');
    const [extractorPassword, setExtractorPassword] = useState('');
    const [clientPalette, setClientPalette] =
        useState<ClientPalette>(initialPalette);
    const [usageHistory, setUsageHistory] = useState(false);
    const [newSoftwares, setNewSoftwares] = useState<string[]>([]);
    const [modules, setModules] = useState<ModuleT[]>([]);
    const [extraFeatures, setExtraFeatures] = useState<ExtraFeatureT[]>([]);
    const [disabled, setDisabled] = useState(false);

    const createClientMutation = useCreateClientMutation(onClose);

    useEffect(() => {
        setDisabled(!name || !bind || !decisionPassword || !extractorPassword);
    }, [name, bind, decisionPassword, extractorPassword]);

    const formatBind = (bindValue: string) => bindValue.replace('-', '_');

    const onSave = () => {
        createClientMutation.mutate({
            body: {
                name,
                bind: formatBind(bind),
                teamPassword: decisionPassword,
                extractorPassword,
                logoUrl: clientPalette.logoUrl,
                primaryColor: clientPalette.primaryColor,
                secondaryColor: clientPalette.secondaryColor,
                extractors: [],
                modules,
                extraFeatures,
                usageHistory,
            },
            softwares: newSoftwares,
        });
    };

    return (
        <Dialog fullWidth maxWidth="lg" open={isOpen} onClose={onClose}>
            <SupportModalTitle
                title={t('support.clientDetails.title')}
                onClose={onClose}
            />
            <DialogContent sx={{ p: '2em' }}>
                {createClientMutation.isPending ? (
                    <Loading />
                ) : (
                    <Flex
                        margin="20px"
                        direction="column"
                        justify="space-between"
                    >
                        <StyItem>
                            <ClientDetailsForm
                                bind={bind}
                                setBind={setBind}
                                setName={setName}
                            />
                        </StyItem>
                        <StyItem>
                            <CustomizationForm
                                palette={clientPalette}
                                setPalette={setClientPalette}
                            />
                        </StyItem>
                        <StyItem>
                            <Typography variant="h5">
                                {t('support.clientConfiguration.modules')}
                            </Typography>
                            <Flex
                                margin="10px"
                                direction="column"
                                alignItems="flex-start"
                            >
                                <ModuleForm
                                    modules={modules}
                                    setModules={setModules}
                                    extraFeatures={extraFeatures}
                                    setExtraFeatures={setExtraFeatures}
                                />
                            </Flex>
                        </StyItem>
                        <StyItem>
                            <Typography variant="h5">
                                {t(
                                    'support.clientConfiguration.integrations.title',
                                )}
                            </Typography>
                            <IntegrationForm
                                bind={bind}
                                newSoftwares={newSoftwares}
                                setNewSoftwares={setNewSoftwares}
                                isNew
                            />
                        </StyItem>
                        <StyItem>
                            <Typography variant="h4">
                                {t('support.clientConfiguration.usageHistory')}
                            </Typography>
                            <UsageHistoryForm
                                usageHistory={usageHistory}
                                setUsageHistory={setUsageHistory}
                            />
                        </StyItem>
                        <StyItem>
                            <Typography variant="h5">
                                {t('support.clientDetails.supportTitle')}
                            </Typography>
                            <ClientPassword
                                setDecisionPassword={setDecisionPassword}
                                setExtractionPassword={setExtractorPassword}
                            />
                        </StyItem>
                        <StyItem>
                            <Button
                                sx={{ m: 1, height: '1rem', width: '300px' }}
                                variant="contained"
                                color="primary"
                                onClick={onSave}
                                disabled={disabled}
                            >
                                {t('common.save')}
                            </Button>
                        </StyItem>
                    </Flex>
                )}
            </DialogContent>
        </Dialog>
    );
};
